import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Camera, ExternalLink } from 'lucide-react';
import BannerUploadModal from './BannerUploadModal';

interface BannerSrcset {
  [width: string]: string;
}

interface ArtistBannerProps {
  artistName: string;
  artistNumber?: number | null;
  bannerUrl: string | null;
  bannerSrcset: BannerSrcset | null;
  slug: string;
  onBannerUpdate: (newUrl: string, srcset: BannerSrcset | null) => void;
}

/**
 * Builds an HTML srcset string from the srcset map + original URL.
 * e.g. "url768 768w, url1200 1200w, urlOriginal 1920w"
 */
function buildSrcSetString(srcset: BannerSrcset, originalUrl: string, originalWidth: number): string {
  const entries: string[] = [];

  // Add variant widths (sorted ascending)
  const widths = Object.keys(srcset).map(Number).sort((a, b) => a - b);
  for (const w of widths) {
    const url = srcset[String(w)];
    if (url) {
      entries.push(`${url} ${w}w`);
    }
  }

  // Add original as the largest
  entries.push(`${originalUrl} ${originalWidth}w`);

  return entries.join(', ');
}

export default function ArtistBanner({ artistName, artistNumber, bannerUrl, bannerSrcset, slug, onBannerUpdate }: ArtistBannerProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentBannerUrl, setCurrentBannerUrl] = useState(bannerUrl);
  const [currentSrcset, setCurrentSrcset] = useState<BannerSrcset | null>(bannerSrcset);

  // Update internal state when prop changes
  if (bannerUrl !== null && bannerUrl !== currentBannerUrl && currentBannerUrl === null) {
    setCurrentBannerUrl(bannerUrl);
    setCurrentSrcset(bannerSrcset);
  }

  const handleUploadComplete = useCallback((newUrl: string, srcset: BannerSrcset | null) => {
    setCurrentBannerUrl(newUrl);
    setCurrentSrcset(srcset);
    onBannerUpdate(newUrl, srcset);
  }, [onBannerUpdate]);

  // Build srcset attribute string
  const srcSetString = currentSrcset && currentBannerUrl
    ? buildSrcSetString(currentSrcset, currentBannerUrl, 2400) // Assume original ~2400w for largest descriptor
    : undefined;
  const artistNumberLabel = typeof artistNumber === 'number'
    ? (artistNumber < 101 ? `Founding Artist #${artistNumber}` : `Artist #${artistNumber}`)
    : null;

  return (
    <>
      {/* Banner container - industry standard aspect ratio ~3:1 */}
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: '3 / 1', maxHeight: '400px', minHeight: '200px' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Public profile quick link */}
        <a
          href={`/artist/${encodeURIComponent(slug)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/60 backdrop-blur-md border border-white/20 text-white text-xs sm:text-sm font-medium hover:bg-black/80 hover:border-white/30 transition-colors shadow-lg"
        >
          <span>View Public Profile</span>
          <ExternalLink className="w-3.5 h-3.5" />
        </a>

        {/* Banner image or placeholder */}
        {currentBannerUrl ? (
          <img
            src={currentBannerUrl}
            srcSet={srcSetString}
            sizes="100vw"
            alt={`${artistName} banner`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
        ) : (
          <img
            src="/fans-audio-default-banner.webp"
            alt={`${artistName} banner`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
        )}

        {/* Gradient overlay at bottom for text readability */}
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />

        {/* Artist name aligned to main content container */}
        <div className="absolute inset-x-0 bottom-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8 lg:pb-10">
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-none"
              style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}
            >
              {artistName}
            </motion.h1>
            {artistNumberLabel && (
              <p
                className="mt-2 text-xs sm:text-sm text-white/75 font-mono tracking-wide"
                style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}
              >
                {artistNumberLabel}
              </p>
            )}
          </div>
        </div>

        {/* Floating edit button - appears on hover, positioned at bottom right */}
        <motion.button
          initial={false}
          animate={{
            opacity: isHovered ? 1 : 0,
            scale: isHovered ? 1 : 0.9,
          }}
          transition={{ duration: 0.2 }}
          onClick={() => setShowUploadModal(true)}
          className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-black/60 backdrop-blur-md border border-white/20 text-white text-sm font-medium hover:bg-black/80 hover:border-white/30 transition-colors cursor-pointer shadow-lg"
          style={{ pointerEvents: isHovered ? 'auto' : 'none' }}
        >
          <Camera className="w-4 h-4" />
          <span>Edit Banner</span>
        </motion.button>
      </div>

      {/* Upload Modal */}
      <BannerUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        slug={slug}
        onUploadComplete={handleUploadComplete}
      />
    </>
  );
}
